/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

/**
 * Role / mailbox local-parts that represent company-level inboxes, not a person.
 * Examples from TASK 59: info@, kontakt@, hr@
 */
const GENERIC_CONTACT_LOCAL_PARTS = new Set([
    'info',
    'kontakt',
    'contact',
    'contacts',
    'hr',
    'hello',
    'office',
    'admin',
    'mail',
    'email',
    'support',
    'sales',
    'reception',
    'empfang',
    'buchhaltung',
    'accounting',
    'billing',
    'invoice',
    'invoices',
    'noreply',
    'no-reply',
    'donotreply',
    'do-not-reply',
    'webmaster',
    'poststelle',
    'sekretariat',
    'assistenz',
    'team',
    'general',
])

export const extractEmailLocalPart = (email?: string | null): string | null => {
    if (!email) {
        return null
    }

    const trimmed = String(email).trim().toLowerCase()
    const atIndex = trimmed.indexOf('@')
    if (atIndex <= 0) {
        return null
    }

    const local = trimmed.slice(0, atIndex).replace(/\+.*$/, '')
    return local || null
}

/** True when the mailbox looks like a company inbox (info@, kontakt@, hr@, …). */
export const isGenericContactEmail = (email?: string | null): boolean => {
    const local = extractEmailLocalPart(email)
    if (!local) {
        return false
    }

    if (GENERIC_CONTACT_LOCAL_PARTS.has(local)) {
        return true
    }

    // e.g. info.zh@firma.ch, kontakt-ch@firma.ch
    const base = local.split(/[._-]/)[0]
    return Boolean(base && GENERIC_CONTACT_LOCAL_PARTS.has(base))
}

export const isGenericCompanyContact = (
    contact: { is_generic?: boolean | null; email?: string | null } | null | undefined
): boolean => {
    if (!contact) {
        return false
    }
    if (contact.is_generic === true) {
        return true
    }
    if (contact.is_generic === false) {
        return false
    }
    return isGenericContactEmail(contact.email)
}

/** Prefer an explicit person; fall back to a generic company contact. */
export const pickPreferredCompanyContact = <
    T extends { id?: number; is_generic?: boolean | null; email?: string | null },
>(
    contacts: T[] | null | undefined,
    preferredId?: number | null
): T | undefined => {
    const list = contacts ?? []
    if (list.length === 0) {
        return undefined
    }

    if (preferredId != null) {
        const preferred = list.find((contact) => contact.id === preferredId)
        if (preferred) {
            return preferred
        }
    }

    const person = list.find((contact) => !isGenericCompanyContact(contact))
    return person ?? list[0]
}

export const formatGenericContactLocalLabel = (email?: string | null): string => {
    const local = extractEmailLocalPart(email)
    if (!local) {
        return ''
    }

    return local
        .split(/[._-]/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}
